import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, Upload, Package, ShoppingCart, DollarSign, AlertTriangle, X, MapPin, Truck, Mail, Printer, Copy } from "lucide-react";
import { MoonMark as Moon } from "@/components/MoonMark";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, mediaUrl, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, COLLECTIONS } from "@/lib/content";
import AdminInbox from "@/pages/admin/AdminInbox";

const CATS = CATEGORIES.map((c) => c.name);
const COLS = COLLECTIONS.map((c) => c.name);

const formatAddress = (a) =>
  !a ? "" : [a.name, a.line1, a.line2, [a.city, a.state, a.postal_code].filter(Boolean).join(", "), a.country, a.phone]
    .filter(Boolean).join("\n");

// New product structure: Colour (sets price) x Chocolate Type. Image order: 0=Natural, 1=Gold, 2=Silver.
const VARIANT_COLORS = [["Gold", 24.99, 1], ["Silver", 24.99, 2], ["Natural", 19.99, 0]];
const VARIANT_TYPES = ["Milk", "Dark", "White"];
const CCODE = { Gold: "GLD", Silver: "SLV", Natural: "NAT" };
const TCODE = { Milk: "MLK", Dark: "DRK", White: "WHT" };
const skuPrefix = (name) => ((name || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 6) || "BOX");
const buildDefaultVariants = () => {
  const v = [];
  for (const [color, price, idx] of VARIANT_COLORS)
    for (const t of VARIANT_TYPES)
      v.push({ sku: "", color, chocolate_type: t, price, stock: 0, sold: 0, image_index: idx });
  return v;
};

const EMPTY = {
  name: "", short_description: "", description: "", price: "", category: "Boxes",
  collection: "", images: [], stock: 0, featured: false, is_gift: false, active: true, variants: [],
};

const IMAGE_SLOTS = ["Natural (1st)", "Gold (2nd)", "Silver (3rd)"];

const field = "w-full bg-noir border border-white/15 px-3 py-2.5 font-body text-sm text-cream placeholder:text-white/30 focus:border-gold focus:outline-none transition-colors";

function ProductDialog({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    setForm(editing
      ? { ...EMPTY, ...editing, price: editing.price }
      : { ...EMPTY, variants: buildDefaultVariants() });
  }, [editing, open]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, res.data.url] }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      stock: parseInt(form.stock) || 0,
      collection: form.collection || null,
      variants: (form.variants || []).map((v) => ({
        ...v,
        sku: v.sku || `${skuPrefix(form.name)}-${CCODE[v.color]}-${TCODE[v.chocolate_type]}`,
        price: parseFloat(v.price) || 0,
        stock: parseInt(v.stock) || 0,
        sold: parseInt(v.sold) || 0,
        image_index: parseInt(v.image_index) || 0,
      })),
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setVariant = (idx, field, value) =>
    setForm((f) => ({ ...f, variants: f.variants.map((v, i) => (i === idx ? { ...v, [field]: value } : v)) }));

  const hasVariants = (form.variants || []).length > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-noir-2 border border-white/10 text-cream max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog" aria-describedby="product-dialog-desc">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-cream">{editing ? "Edit Product" : "New Product"}</DialogTitle>
        </DialogHeader>
        <span id="product-dialog-desc" className="sr-only">Create or edit a product, its images and per-variant inventory</span>
        <form onSubmit={save} className="space-y-4 mt-2">
          <input required placeholder="Product name" value={form.name} data-testid="pf-name"
            onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
          <input placeholder="Short description" value={form.short_description} data-testid="pf-short"
            onChange={(e) => setForm({ ...form, short_description: e.target.value })} className={field} />
          <textarea rows={3} placeholder="Full description" value={form.description} data-testid="pf-desc"
            onChange={(e) => setForm({ ...form, description: e.target.value })} className={field} />

          {!hasVariants && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-body text-xs text-white/50 mb-1 block">Price (USD)</label>
              <input type="number" step="0.01" required placeholder="0.00" value={form.price} data-testid="pf-price"
                onChange={(e) => setForm({ ...form, price: e.target.value })} className={field} />
            </div>
            <div>
              <label className="font-body text-xs text-white/50 mb-1 block">Stock</label>
              <input type="number" required placeholder="0" value={form.stock} data-testid="pf-stock"
                onChange={(e) => setForm({ ...form, stock: e.target.value })} className={field} />
            </div>
          </div>
          )}

          {hasVariants && (
            <div data-testid="pf-variants">
              <label className="font-body text-xs text-white/50 mb-2 block">
                Variant Inventory — price set by colour, stock tracked per option
              </label>
              <div className="border border-white/10 divide-y divide-white/10">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-noir font-body text-[10px] uppercase tracking-widest text-white/40">
                  <span className="col-span-3">Colour</span>
                  <span className="col-span-3">Type</span>
                  <span className="col-span-3">Price</span>
                  <span className="col-span-3">Stock</span>
                </div>
                {form.variants.map((v, i) => (
                  <div key={v.sku} className="grid grid-cols-12 gap-2 px-3 py-2 items-center" data-testid={`pf-variant-${v.sku}`}>
                    <span className="col-span-3 font-body text-xs text-cream">{v.color}</span>
                    <span className="col-span-3 font-body text-xs text-white/60">{v.chocolate_type}</span>
                    <input type="number" step="0.01" value={v.price} data-testid={`pf-variant-price-${v.sku}`}
                      onChange={(e) => setVariant(i, "price", e.target.value)}
                      className="col-span-3 bg-noir border border-white/15 px-2 py-1.5 font-body text-xs text-gold focus:border-gold focus:outline-none" />
                    <input type="number" value={v.stock} data-testid={`pf-variant-stock-${v.sku}`}
                      onChange={(e) => setVariant(i, "stock", e.target.value)}
                      className="col-span-3 bg-noir border border-white/15 px-2 py-1.5 font-body text-xs text-cream focus:border-gold focus:outline-none" />
                  </div>
                ))}
              </div>
              <p className="font-body text-[11px] text-white/35 mt-2">
                Total stock: {form.variants.reduce((a, v) => a + (parseInt(v.stock) || 0), 0)} · Sold out when a variant reaches 0.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-body text-xs text-white/50 mb-1 block">Category</label>
              <select value={form.category} data-testid="pf-category" onChange={(e) => setForm({ ...form, category: e.target.value })} className={field}>
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="font-body text-xs text-white/50 mb-1 block">Collection</label>
              <select value={form.collection || ""} data-testid="pf-collection" onChange={(e) => setForm({ ...form, collection: e.target.value })} className={field}>
                <option value="">None</option>
                {COLS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="font-body text-xs text-white/50 mb-1 block">Images</label>
            <p className="font-body text-[11px] text-white/35 mb-2">Upload in this order — 1st = Natural, 2nd = Gold, 3rd = Silver. The colour a shopper picks shows its matching image.</p>
            <div className="flex flex-wrap gap-3 mb-3">
              {form.images.map((img, i) => (
                <div key={i} className="relative w-20 h-24 border border-white/10">
                  <img src={mediaUrl(img)} alt="" className="w-full h-20 object-cover" />
                  <span className="block text-center font-body text-[9px] text-champagne/80 py-0.5">{IMAGE_SLOTS[i] || `#${i + 1}`}</span>
                  <button type="button" onClick={() => setForm({ ...form, images: form.images.filter((_, idx) => idx !== i) })}
                    className="absolute -top-2 -right-2 bg-black border border-gold/40 rounded-full p-0.5" data-testid={`remove-img-${i}`}>
                    <X className="w-3 h-3 text-gold" />
                  </button>
                </div>
              ))}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="pf-file" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="inline-flex items-center gap-2 border border-gold/40 text-gold font-body uppercase tracking-widest text-[11px] px-4 py-2.5 hover:bg-gold/10 transition-colors disabled:opacity-50" data-testid="pf-upload-btn">
              <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload image"}
            </button>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 font-body text-sm text-white/70">
              <input type="checkbox" checked={form.active} data-testid="pf-active" onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#D4AF37]" /> Active
            </label>
          </div>

          <button type="submit" disabled={saving} data-testid="pf-save"
            className="w-full py-3.5 bg-gold text-black font-body uppercase tracking-[0.2em] text-xs hover:bg-champagne transition-colors disabled:opacity-50">
            {saving ? "Saving…" : editing ? "Update Product" : "Create Product"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OrderDetailDialog({ order, onClose, onChanged }) {
  const [conf, setConf] = useState(null);
  const [sending, setSending] = useState(false);
  const [emailForm, setEmailForm] = useState(null); // { subject, body }
  const [emailing, setEmailing] = useState(false);
  const [ship, setShip] = useState({ carrier: "", tracking_number: "", tracking_url: "" });
  const [shipping, setShipping] = useState(false);
  const [shippedInfo, setShippedInfo] = useState(null);
  useEffect(() => {
    setConf(order ? order.confirmation_email : null); setEmailForm(null);
    setShippedInfo(order ? order.shipped_email : null);
    setShip({ carrier: order?.carrier || "", tracking_number: order?.tracking_number || "", tracking_url: order?.tracking_url || "" });
  }, [order]);
  if (!order) return null;
  const addr = order.shipping_address;
  const addrLines = addr
    ? [addr.name, addr.line1, addr.line2, [addr.city, addr.state, addr.postal_code].filter(Boolean).join(", "), addr.country, addr.phone && `☎ ${addr.phone}`].filter(Boolean)
    : [];

  const previewEmail = () => {
    if (!conf?.html) return;
    const w = window.open("", "_blank");
    if (w) { w.document.write(conf.html); w.document.close(); }
  };
  const sendEmail = async () => {
    setSending(true);
    try {
      const r = await api.post(`/admin/orders/${order.id}/send-confirmation`);
      setConf(r.data.confirmation_email);
      toast.success(r.data.confirmation_email.sent ? "Confirmation sent" : "Copy saved (email not sent — see status)");
    } catch { toast.error("Could not send confirmation"); }
    finally { setSending(false); }
  };

  const openEmail = () => setEmailForm({
    subject: `Regarding your After Dark order #${order.id.slice(0, 8)}`,
    body: "",
  });
  const sendCustomerEmail = async () => {
    if (!emailForm.body.trim()) return toast.error("Write a message first");
    setEmailing(true);
    try {
      const fd = new FormData();
      fd.append("to", order.customer_email);
      fd.append("subject", emailForm.subject);
      fd.append("body_text", emailForm.body);
      fd.append("body_html", `<div style="font-family:Georgia,serif;white-space:pre-wrap;">${emailForm.body.replace(/</g, "&lt;")}</div>`);
      await api.post("/admin/mail/send", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Email sent to customer");
      setEmailForm(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send email");
    } finally { setEmailing(false); }
  };

  const sendShipped = async () => {
    setShipping(true);
    try {
      const r = await api.post(`/admin/orders/${order.id}/send-shipped`, {
        carrier: ship.carrier || null,
        tracking_number: ship.tracking_number || null,
        tracking_url: ship.tracking_url || null,
      });
      setShippedInfo(r.data.shipped_email);
      toast.success(r.data.shipped_email.sent ? "Shipped email sent" : "Marked shipped (email not sent — see status)");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send shipped email");
    } finally { setShipping(false); }
  };

  const printPackingSlip = () => {
    const shortId = order.id.slice(0, 8).toUpperCase();
    const date = new Date(order.created_at).toLocaleDateString();
    const shipTo = addrLines.length
      ? addrLines.map((l) => `<div>${l}</div>`).join("")
      : `<div>${order.customer_email || "Address collected at payment"}</div>`;
    const rows = (order.items || []).map((i) => {
      const variant = [i.color, i.chocolate_type && `${i.chocolate_type} Chocolate`].filter(Boolean).join(" · ");
      return `<tr>
        <td class="qty">${i.quantity}</td>
        <td class="item"><div class="name">${i.name || "Item"}</div>${variant ? `<div class="variant">${variant}</div>` : ""}</td>
        <td class="sku">${i.variant_sku || ""}</td>
      </tr>`;
    }).join("");
    const totalUnits = (order.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Packing Slip ${shortId}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: Georgia, 'Times New Roman', serif; color: #111; margin: 0; padding: 40px; }
        .head { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #111; padding-bottom:16px; }
        .brand { font-size:20px; letter-spacing:4px; font-weight:bold; text-transform:uppercase; }
        .brand small { display:block; font-size:10px; letter-spacing:2px; font-weight:normal; color:#555; margin-top:4px; }
        .slip-title { text-align:right; }
        .slip-title h1 { font-size:16px; letter-spacing:3px; margin:0; text-transform:uppercase; }
        .slip-title div { font-size:12px; color:#444; margin-top:4px; }
        .meta { display:flex; justify-content:space-between; margin-top:28px; gap:40px; }
        .meta h2 { font-size:10px; letter-spacing:2px; text-transform:uppercase; color:#666; margin:0 0 8px; }
        .meta .block { font-size:13px; line-height:1.5; }
        table { width:100%; border-collapse:collapse; margin-top:32px; }
        thead th { text-align:left; font-size:10px; letter-spacing:2px; text-transform:uppercase; color:#666; border-bottom:1px solid #111; padding:8px 6px; }
        tbody td { padding:12px 6px; border-bottom:1px solid #ddd; font-size:14px; vertical-align:top; }
        td.qty { width:44px; font-weight:bold; }
        td.sku { width:120px; color:#777; font-size:12px; }
        .name { font-weight:bold; }
        .variant { color:#666; font-size:12px; margin-top:2px; }
        .foot { margin-top:36px; display:flex; justify-content:space-between; align-items:flex-end; }
        .units { font-size:13px; }
        .note { font-size:12px; color:#555; max-width:280px; font-style:italic; }
        @media print { body { padding:24px; } .noprint { display:none; } }
        .noprint { margin-top:40px; text-align:center; }
        .noprint button { font-family:inherit; padding:10px 24px; font-size:12px; letter-spacing:2px; text-transform:uppercase; cursor:pointer; border:1px solid #111; background:#111; color:#fff; }
      </style></head>
      <body>
        <div class="head">
          <div class="brand">After Dark<small>Discreet Fulfilment</small></div>
          <div class="slip-title"><h1>Packing Slip</h1><div>Order #${shortId}</div><div>${date}</div></div>
        </div>
        <div class="meta">
          <div><h2>Ship To</h2><div class="block">${shipTo}</div></div>
          <div><h2>Shipping Method</h2><div class="block">${order.shipping_label || order.shipping_option || "Standard"}</div></div>
        </div>
        <table>
          <thead><tr><th>Qty</th><th>Item</th><th>Ref</th></tr></thead>
          <tbody>${rows || `<tr><td colspan="3">No items</td></tr>`}</tbody>
        </table>
        <div class="foot">
          <div class="units">Total items: <strong>${totalUnits}</strong></div>
          <div class="note">Thank you for your order. Packed with care and delivered discreetly.</div>
        </div>
        <div class="noprint"><button onclick="window.print()">Print</button></div>
      </body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 400); }
    else { toast.error("Allow pop-ups to print the packing slip"); }
  };
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-noir-2 border border-white/10 text-cream max-w-lg max-h-[90vh] overflow-y-auto" data-testid="order-detail-dialog" aria-describedby="order-detail-desc">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-cream">Order #{order.id.slice(0, 8)}</DialogTitle>
        </DialogHeader>
        <p id="order-detail-desc" className="font-body text-xs text-white/40 -mt-1">
          {new Date(order.created_at).toLocaleString()}
        </p>

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <span className={`font-body text-[11px] uppercase tracking-widest px-2.5 py-1 border ${order.payment_status === "paid" ? "border-gold/50 text-gold" : "border-white/15 text-white/40"}`}>
            {order.payment_status}
          </span>
          <span className="font-body text-[11px] uppercase tracking-widest px-2.5 py-1 border border-white/15 text-white/60">
            {order.fulfillment_status || "unfulfilled"}
          </span>
          <button onClick={printPackingSlip} data-testid="print-packing-slip-btn"
            className="ml-auto inline-flex items-center gap-1.5 border border-gold/50 text-gold font-body uppercase tracking-[0.15em] text-[10px] px-3 py-1.5 hover:bg-gold hover:text-black transition-colors">
            <Printer className="w-3 h-3" /> Packing Slip
          </button>
        </div>

        <div className="mt-5 space-y-1">
          <p className="font-body uppercase tracking-[0.2em] text-[11px] text-champagne mb-1">Customer</p>
          <p className="font-body text-sm text-white/70" data-testid="order-detail-email">{order.customer_email || "—"}</p>
          {order.marketing_opt_in && <p className="font-body text-[11px] text-gold/70">✓ Opted in to marketing</p>}
        </div>

        <div className="mt-5">
          <p className="font-body uppercase tracking-[0.2em] text-[11px] text-champagne mb-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gold" /> Shipping Address</p>
          {addrLines.length ? (
            <div className="font-body text-sm text-white/70 leading-relaxed" data-testid="order-detail-address">
              {addrLines.map((l, i) => <p key={i}>{l}</p>)}
            </div>
          ) : <p className="font-body text-sm text-white/40">Collected at payment (not yet available)</p>}
        </div>

        <div className="mt-5">
          <p className="font-body uppercase tracking-[0.2em] text-[11px] text-champagne mb-2 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-gold" /> Shipping Service</p>
          <p className="font-body text-sm text-white/70" data-testid="order-detail-shipping">
            {order.shipping_label || order.shipping_option} — {order.shipping_cost === 0 ? "Free" : money(order.shipping_cost)}
          </p>
        </div>

        <div className="mt-5">
          <p className="font-body uppercase tracking-[0.2em] text-[11px] text-champagne mb-2">Items</p>
          <div className="space-y-2" data-testid="order-detail-items">
            {(order.items || []).map((i, idx) => (
              <div key={idx} className="flex justify-between gap-3 border-b border-white/10 pb-2">
                <div>
                  <p className="font-body text-sm text-cream">{i.name}</p>
                  {(i.color || i.chocolate_type) && (
                    <p className="font-body text-[11px] text-white/40">{[i.color, i.chocolate_type && `${i.chocolate_type} Chocolate`].filter(Boolean).join(" · ")}{i.variant_sku ? ` · ${i.variant_sku}` : ""}</p>
                  )}
                </div>
                <p className="font-body text-sm text-white/60 whitespace-nowrap">{money(i.price)} × {i.quantity}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 border-t border-white/10 pt-4 space-y-1.5 font-body text-sm">
          <div className="flex justify-between text-white/60"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
          <div className="flex justify-between text-white/60"><span>Shipping</span><span>{order.shipping_cost === 0 ? "Free" : money(order.shipping_cost)}</span></div>
          <div className="flex justify-between text-cream text-lg pt-2 border-t border-white/10 mt-2"><span className="font-serif">Total</span><span data-testid="order-detail-total">{money(order.total)}</span></div>
        </div>

        <div className="mt-5 border-t border-white/10 pt-4" data-testid="order-shipped-section">
          <p className="font-body uppercase tracking-[0.2em] text-[11px] text-champagne mb-2 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-gold" /> Shipping Notification</p>
          {shippedInfo && (
            <p className="font-body text-xs mb-2" data-testid="shipped-status">
              {shippedInfo.sent
                ? <span className="text-gold">Shipped email sent{shippedInfo.tracking_number ? ` · ${shippedInfo.carrier ? shippedInfo.carrier + " " : ""}${shippedInfo.tracking_number}` : ""}</span>
                : <span className="text-white/50">Not sent — {shippedInfo.error || "pending"}</span>}
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <input value={ship.carrier} onChange={(e) => setShip({ ...ship, carrier: e.target.value })} placeholder="Carrier (e.g. USPS)" data-testid="ship-carrier"
              className="bg-noir border border-white/15 px-3 py-2 font-body text-xs text-cream focus:border-gold focus:outline-none" />
            <input value={ship.tracking_number} onChange={(e) => setShip({ ...ship, tracking_number: e.target.value })} placeholder="Tracking number" data-testid="ship-tracking"
              className="bg-noir border border-white/15 px-3 py-2 font-body text-xs text-cream focus:border-gold focus:outline-none" />
          </div>
          <input value={ship.tracking_url} onChange={(e) => setShip({ ...ship, tracking_url: e.target.value })} placeholder="Tracking URL (optional)" data-testid="ship-url"
            className="w-full mt-2 bg-noir border border-white/15 px-3 py-2 font-body text-xs text-cream focus:border-gold focus:outline-none" />
          <button onClick={sendShipped} disabled={shipping || !order.customer_email} data-testid="send-shipped-btn"
            className="mt-3 inline-flex items-center gap-1.5 bg-gold text-black font-body uppercase tracking-[0.15em] text-[10px] px-3 py-2 hover:bg-champagne transition-colors disabled:opacity-40">
            <Truck className="w-3 h-3" /> {shipping ? "Sending…" : (shippedInfo?.sent ? "Resend Shipped Email" : "Mark Shipped & Email Customer")}
          </button>
          {!order.customer_email && <p className="font-body text-[11px] text-white/30 mt-1">No customer email on this order.</p>}
        </div>


        <div className="mt-5 border-t border-white/10 pt-4" data-testid="order-confirmation-section">
          <p className="font-body uppercase tracking-[0.2em] text-[11px] text-champagne mb-2 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-gold" /> Confirmation Email</p>
          {conf ? (
            <div className="font-body text-xs text-white/60 space-y-1">
              <p>Status: {conf.sent
                ? <span className="text-gold" data-testid="conf-status">Sent{conf.email_id ? ` · ${conf.email_id.slice(0, 10)}…` : ""}</span>
                : <span className="text-white/50" data-testid="conf-status">Not sent — {conf.error || "pending"}</span>}</p>
              <p>To: {conf.to || "—"}</p>
            </div>
          ) : <p className="font-body text-xs text-white/40" data-testid="conf-status">No confirmation generated yet.</p>}
          <div className="flex gap-3 mt-3">
            <button onClick={sendEmail} disabled={sending} data-testid="send-confirmation-btn"
              className="inline-flex items-center gap-1.5 border border-gold/50 text-gold font-body uppercase tracking-[0.15em] text-[10px] px-3 py-2 hover:bg-gold hover:text-black transition-colors disabled:opacity-40">
              <Mail className="w-3 h-3" /> {sending ? "Sending…" : (conf?.sent ? "Resend" : "Send / Save Copy")}
            </button>
            {conf?.html && (
              <button onClick={previewEmail} data-testid="preview-confirmation-btn"
                className="inline-flex items-center gap-1.5 border border-white/20 text-white/70 font-body uppercase tracking-[0.15em] text-[10px] px-3 py-2 hover:border-gold hover:text-gold transition-colors">
                Preview Copy
              </button>
            )}
          </div>
        </div>

        <div className="mt-5 border-t border-white/10 pt-4" data-testid="order-email-section">
          <p className="font-body uppercase tracking-[0.2em] text-[11px] text-champagne mb-2 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-gold" /> Email Customer</p>
          {!order.customer_email ? (
            <p className="font-body text-xs text-white/40">No customer email on this order.</p>
          ) : !emailForm ? (
            <button onClick={openEmail} data-testid="email-customer-btn"
              className="inline-flex items-center gap-1.5 border border-gold/50 text-gold font-body uppercase tracking-[0.15em] text-[10px] px-3 py-2 hover:bg-gold hover:text-black transition-colors">
              <Mail className="w-3 h-3" /> Email {order.customer_email}
            </button>
          ) : (
            <div className="space-y-2">
              <input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                data-testid="email-customer-subject"
                className="w-full bg-noir border border-white/15 px-3 py-2 font-body text-xs text-cream focus:border-gold focus:outline-none" />
              <textarea rows={5} value={emailForm.body} placeholder={`Write to ${order.customer_email}…`}
                onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })} data-testid="email-customer-body"
                className="w-full bg-noir border border-white/15 px-3 py-2 font-body text-xs text-cream focus:border-gold focus:outline-none" />
              <div className="flex gap-2">
                <button onClick={sendCustomerEmail} disabled={emailing} data-testid="email-customer-send"
                  className="inline-flex items-center gap-1.5 bg-gold text-black font-body uppercase tracking-[0.15em] text-[10px] px-3 py-2 hover:bg-champagne transition-colors disabled:opacity-50">
                  {emailing ? "Sending…" : "Send Email"}
                </button>
                <button onClick={() => setEmailForm(null)}
                  className="font-body uppercase tracking-[0.15em] text-[10px] text-white/50 hover:text-cream px-3 py-2">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


export default function AdminDashboard() {
  const { admin, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [settings, setSettings] = useState({ announcement_text: "", announcement_enabled: true, announcements: [], free_shipping_threshold: 75, shipping_rates: [] });
  const [savingSettings, setSavingSettings] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderFilter, setOrderFilter] = useState("all");
  const [orderSort, setOrderSort] = useState("newest");

  useEffect(() => {
    if (!loading && !admin) navigate("/admin/login");
  }, [loading, admin, navigate]);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("Settings saved");
    } catch { toast.error("Could not save settings"); }
    finally { setSavingSettings(false); }
  };

  const setRate = (idx, key, value) =>
    setSettings((s) => ({ ...s, shipping_rates: (s.shipping_rates || []).map((r, i) => (i === idx ? { ...r, [key]: value } : r)) }));

  const setAnnouncement = (idx, key, value) =>
    setSettings((s) => ({ ...s, announcements: (s.announcements || []).map((a, i) => (i === idx ? { ...a, [key]: value } : a)) }));
  const addAnnouncement = () =>
    setSettings((s) => ({ ...s, announcements: [...(s.announcements || []), { id: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())), text: "", enabled: true }] }));
  const removeAnnouncement = (idx) =>
    setSettings((s) => ({ ...s, announcements: (s.announcements || []).filter((_, i) => i !== idx) }));

  useEffect(() => { if (admin) loadAll(); }, [admin]);

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Product deleted");
      loadAll();
    } catch { toast.error("Delete failed"); }
  };

  const setOrderStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}`, { status });
      toast.success("Order updated");
      loadAll();
    } catch { toast.error("Update failed"); }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("Delete this order permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success("Order deleted");
      setOrders((os) => os.filter((o) => o.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  if (loading || !admin) return <p className="text-center font-serif text-2xl text-white/40 py-32">Loading…</p>;

  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "products", label: "Products" },
    { id: "orders", label: "Orders" },
    { id: "inbox", label: "Inbox" },
    { id: "settings", label: "Settings" },
  ];

  const statCards = stats ? [
    { icon: DollarSign, label: "Revenue", value: money(stats.revenue) },
    { icon: ShoppingCart, label: "Paid Orders", value: stats.order_count },
    { icon: Package, label: "Products", value: stats.product_count },
    { icon: AlertTriangle, label: "Low Stock", value: stats.low_stock },
  ] : [];

  return (
    <div className="min-h-screen bg-noir" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-black/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-18 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Moon className="w-6 h-6 text-gold" strokeWidth={1.3} />
            <div>
              <p className="font-serif text-lg text-cream leading-none">After Dark · Admin</p>
              <p className="font-body text-[11px] text-white/40 mt-0.5">{admin.email}</p>
            </div>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout"
            className="inline-flex items-center gap-2 font-body uppercase tracking-widest text-[11px] text-white/60 hover:text-gold">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-2 border-b border-white/10 mb-8">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`px-5 py-3 font-body uppercase tracking-[0.15em] text-xs transition-colors border-b-2 -mb-px ${tab === t.id ? "border-gold text-gold" : "border-transparent text-white/50 hover:text-cream"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="overview-panel">
            {statCards.map((s) => (
              <div key={s.label} className="border border-white/10 bg-noir-2 p-6">
                <s.icon className="w-6 h-6 text-gold mb-4" strokeWidth={1.3} />
                <p className="font-serif text-3xl text-cream">{s.value}</p>
                <p className="font-body text-xs uppercase tracking-widest text-white/40 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "products" && (
          <div data-testid="products-panel">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl text-cream">Products ({products.length})</h2>
              <button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-product-btn"
                className="inline-flex items-center gap-2 bg-gold text-black font-body uppercase tracking-widest text-[11px] px-4 py-2.5 hover:bg-champagne transition-colors">
                <Plus className="w-4 h-4" /> New Product
              </button>
            </div>
            <div className="border border-white/10 divide-y divide-white/10">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`admin-product-${p.id}`}>
                  <div className="w-14 h-16 bg-noir-2 overflow-hidden shrink-0">
                    {(p.images || [])[0] && <img src={mediaUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg text-cream truncate">{p.name}</p>
                    <p className="font-body text-xs text-white/40">{p.category}{p.collection ? ` · ${p.collection}` : ""}</p>
                  </div>
                  <div className="hidden sm:block text-right">
                    <p className="font-body text-sm text-gold">{money(p.price)}</p>
                    <p className={`font-body text-xs ${p.stock <= 0 ? "text-red-400" : p.stock <= 5 ? "text-gold" : "text-white/40"}`}>
                      {p.stock <= 0 ? "Sold out" : `${p.stock} in stock`} · {p.sold || 0} sold
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditing(p); setDialogOpen(true); }} data-testid={`edit-${p.id}`}
                      className="p-2 border border-white/15 hover:border-gold/50 hover:text-gold text-white/60"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(p.id)} data-testid={`delete-${p.id}`}
                      className="p-2 border border-white/15 hover:border-red-400/50 hover:text-red-400 text-white/60"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {products.length === 0 && <p className="p-8 text-center font-body text-white/40">No products yet.</p>}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="orders-panel">
            <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
              <h2 className="font-serif text-2xl text-cream">Orders ({orders.length})</h2>
              <div className="flex items-center gap-3">
                <select value={orderFilter} onChange={(e) => setOrderFilter(e.target.value)} data-testid="order-filter"
                  className="bg-noir border border-white/15 px-3 py-2 font-body text-xs text-cream focus:border-gold focus:outline-none">
                  <option value="all">All orders</option>
                  <option value="needs-fulfilment">Needs fulfilment (paid + unfulfilled)</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Payment pending</option>
                  <option value="unfulfilled">Unfulfilled</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                </select>
                <select value={orderSort} onChange={(e) => setOrderSort(e.target.value)} data-testid="order-sort"
                  className="bg-noir border border-white/15 px-3 py-2 font-body text-xs text-cream focus:border-gold focus:outline-none">
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="total-desc">Total: high to low</option>
                  <option value="total-asc">Total: low to high</option>
                </select>
              </div>
            </div>
            {(() => {
              let list = orders.filter((o) => {
                if (orderFilter === "all") return true;
                if (orderFilter === "needs-fulfilment") return o.payment_status === "paid" && (o.fulfillment_status || "unfulfilled") === "unfulfilled";
                if (orderFilter === "paid") return o.payment_status === "paid";
                if (orderFilter === "pending") return o.payment_status !== "paid";
                return (o.fulfillment_status || "unfulfilled") === orderFilter;
              });
              list = [...list].sort((a, b) => {
                if (orderSort === "newest") return new Date(b.created_at) - new Date(a.created_at);
                if (orderSort === "oldest") return new Date(a.created_at) - new Date(b.created_at);
                if (orderSort === "total-desc") return (b.total || 0) - (a.total || 0);
                if (orderSort === "total-asc") return (a.total || 0) - (b.total || 0);
                return 0;
              });
              return (
                <div className="border border-white/10 divide-y divide-white/10">
                  {list.map((o) => (
                    <div key={o.id} onClick={() => setSelectedOrder(o)} role="button" tabIndex={0}
                      className="p-4 cursor-pointer hover:bg-white/5 transition-colors" data-testid={`admin-order-${o.id}`}>
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div>
                          <p className="font-body text-sm text-cream">#{o.id.slice(0, 8)} · {money(o.total)}</p>
                          <p className="font-body text-xs text-white/40">
                            {o.customer_email || "—"} · {new Date(o.created_at).toLocaleDateString()} · {(o.items || []).length} items
                          </p>
                        </div>
                        <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                          <span className={`font-body text-[11px] uppercase tracking-widest px-2.5 py-1 border ${o.payment_status === "paid" ? "border-gold/50 text-gold" : "border-white/15 text-white/40"}`}>
                            {o.payment_status}
                          </span>
                          <select value={o.fulfillment_status || "unfulfilled"} onChange={(e) => setOrderStatus(o.id, e.target.value)}
                            data-testid={`order-status-${o.id}`} className="bg-noir border border-white/15 px-2 py-1.5 font-body text-xs text-cream focus:border-gold focus:outline-none">
                            <option value="unfulfilled">Unfulfilled</option>
                            <option value="processing">Processing</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                          </select>
                          <button onClick={() => deleteOrder(o.id)} data-testid={`delete-order-${o.id}`} title="Delete order"
                            className="border border-white/15 text-white/50 p-1.5 hover:border-red-400 hover:text-red-400 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <p className="font-body text-xs text-white/50 mt-2">{(o.items || []).map((i) => `${i.name} ×${i.quantity}`).join(", ")}</p>
                      {o.shipping_address ? (
                        <div className="flex items-start gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                          <MapPin className="w-3.5 h-3.5 text-gold/70 mt-0.5 shrink-0" />
                          <p className="font-body text-xs text-white/45 whitespace-pre-line flex-1" data-testid={`order-row-address-${o.id}`}>
                            {formatAddress(o.shipping_address)}
                          </p>
                          <button
                            onClick={() => { navigator.clipboard?.writeText(formatAddress(o.shipping_address)); toast.success("Address copied"); }}
                            data-testid={`copy-address-${o.id}`} title="Copy address"
                            className="border border-white/15 text-white/50 p-1.5 hover:border-gold hover:text-gold transition-colors shrink-0">
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <p className="font-body text-[11px] text-white/25 mt-2">No shipping address on file</p>
                      )}
                    </div>
                  ))}
                  {list.length === 0 && <p className="p-8 text-center font-body text-white/40">No orders match.</p>}
                </div>
              );
            })()}
          </div>
        )}

        {tab === "inbox" && <AdminInbox />}

        {tab === "settings" && (
          <div className="max-w-2xl space-y-8" data-testid="settings-panel">
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-2xl text-cream">Announcement Banners</h2>
                <button onClick={addAnnouncement} data-testid="add-announcement-btn"
                  className="inline-flex items-center gap-1.5 border border-gold/50 text-gold font-body uppercase tracking-[0.15em] text-[10px] px-3 py-2 hover:bg-gold hover:text-black transition-colors">
                  <Plus className="w-3 h-3" /> Add Banner
                </button>
              </div>
              <div className="bg-noir-2 border border-white/10 p-7 space-y-5">
                <p className="font-body text-[11px] text-white/40">Enabled banners rotate in the top bar every 5 seconds. Use them for shipping, promotions, updates, etc.</p>
                <div className="space-y-4" data-testid="announcements-editor">
                  {(settings.announcements || []).map((a, i) => (
                    <div key={a.id || i} className="border border-white/10 p-4" data-testid={`announcement-${i}`}>
                      <div className="flex items-start gap-3">
                        <textarea rows={2} value={a.text} placeholder="Banner text…" data-testid={`announcement-text-${i}`}
                          onChange={(e) => setAnnouncement(i, "text", e.target.value)}
                          className="flex-1 bg-noir border border-white/15 px-3 py-2 font-body text-sm text-cream placeholder:text-white/30 focus:border-gold focus:outline-none" />
                        <button onClick={() => removeAnnouncement(i)} data-testid={`announcement-remove-${i}`}
                          className="text-white/40 hover:text-red-400 mt-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                      <label className="flex items-center gap-2 font-body text-xs text-white/60 mt-3">
                        <input type="checkbox" checked={a.enabled} data-testid={`announcement-enabled-${i}`}
                          onChange={(e) => setAnnouncement(i, "enabled", e.target.checked)} className="accent-[#D4AF37]" />
                        Show this banner
                      </label>
                    </div>
                  ))}
                  {(settings.announcements || []).length === 0 && (
                    <p className="font-body text-xs text-white/40">No banners. Add one to display a message at the top of the site.</p>
                  )}
                </div>
                <div className="border border-white/10 bg-noir p-4">
                  <p className="font-body text-[10px] uppercase tracking-widest text-white/40 mb-2">Preview (rotates live)</p>
                  <div className="space-y-1.5">
                    {(settings.announcements || []).filter((a) => a.enabled && a.text.trim()).map((a, i) => (
                      <div key={i} className="bg-gold text-black text-center py-1.5 px-3 font-body uppercase tracking-[0.2em] text-[11px] font-medium">{a.text}</div>
                    ))}
                    {(settings.announcements || []).filter((a) => a.enabled && a.text.trim()).length === 0 && (
                      <p className="font-body text-xs text-white/30">No visible banners</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="font-serif text-2xl text-cream mb-6">Shipping</h2>
              <div className="bg-noir-2 border border-white/10 p-7 space-y-6">
                <div>
                  <label className="font-body text-xs uppercase tracking-widest text-white/50 mb-2 block">Free standard shipping threshold (USD)</label>
                  <input type="number" step="0.01" value={settings.free_shipping_threshold} data-testid="settings-free-threshold"
                    onChange={(e) => setSettings({ ...settings, free_shipping_threshold: parseFloat(e.target.value) || 0 })}
                    className={`${field} max-w-[160px]`} />
                  <p className="font-body text-[11px] text-white/35 mt-2">Orders at or above this qualify for free Standard shipping only.</p>
                </div>

                <div className="space-y-5" data-testid="settings-shipping-rates">
                  {(settings.shipping_rates || []).map((r, i) => (
                    <div key={r.id} className="border border-white/10 p-4" data-testid={`settings-rate-${r.id}`}>
                      <p className="font-body text-[11px] uppercase tracking-widest text-champagne mb-3">{r.id}</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="font-body text-[11px] text-white/50 mb-1 block">Label</label>
                          <input value={r.label} data-testid={`rate-label-${r.id}`}
                            onChange={(e) => setRate(i, "label", e.target.value)} className={field} />
                        </div>
                        <div className="col-span-2">
                          <label className="font-body text-[11px] text-white/50 mb-1 block">Detail</label>
                          <input value={r.detail} data-testid={`rate-detail-${r.id}`}
                            onChange={(e) => setRate(i, "detail", e.target.value)} className={field} />
                        </div>
                        <div>
                          <label className="font-body text-[11px] text-white/50 mb-1 block">Price (USD)</label>
                          <input type="number" step="0.01" value={r.price} data-testid={`rate-price-${r.id}`}
                            onChange={(e) => setRate(i, "price", parseFloat(e.target.value) || 0)} className={field} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="font-body text-[11px] text-white/50 mb-1 block">Min days</label>
                            <input type="number" value={r.min_days} data-testid={`rate-min-${r.id}`}
                              onChange={(e) => setRate(i, "min_days", parseInt(e.target.value) || 0)} className={field} />
                          </div>
                          <div>
                            <label className="font-body text-[11px] text-white/50 mb-1 block">Max days</label>
                            <input type="number" value={r.max_days} data-testid={`rate-max-${r.id}`}
                              onChange={(e) => setRate(i, "max_days", parseInt(e.target.value) || 0)} className={field} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <button onClick={saveSettings} disabled={savingSettings} data-testid="settings-save"
              className="w-full py-3.5 bg-gold text-black font-body uppercase tracking-[0.2em] text-xs hover:bg-champagne transition-colors disabled:opacity-50">
              {savingSettings ? "Saving…" : "Save Settings"}
            </button>
          </div>
        )}
      </div>

      <ProductDialog open={dialogOpen} onClose={() => setDialogOpen(false)} editing={editing} onSaved={loadAll} />
      <OrderDetailDialog order={selectedOrder} onClose={() => setSelectedOrder(null)} onChanged={loadAll} />
    </div>
  );
}
